import { ExportInput } from "@/types/integrations";
import { useMemo } from "react";
import { Focusable } from "react-aria";
import { Controller, useFormContext } from "react-hook-form";
import { AdvancedDateTimeField } from "./AdvancedDateTimeField";
import { Checkbox } from "./Checkbox";
import { CheckCard } from "./CheckCard";
import { DateField } from "./DateField";
import { DatePicker } from "./DatePicker";
import { MultiSelect } from "./MultiSelect";
import { NumberField } from "./NumberField";
import { Select, SelectItem } from "./Select";
import { TagField } from "./TagField";
import { TextField } from "./TextField";
import { Tooltip } from "./Tooltip";

interface DynamicFieldProps {
  field: ExportInput;
  name: string;
  required?: boolean;
}

/**
 * Multiselect values are persisted as a single delimiter-joined string by
 * `formatExportInputs`, so parsing has to mirror that join. Without this the
 * stored string is treated as one opaque key and re-joined on every save.
 */
const VALUE_DELIMITER = "||";

const parseSelectionValue = (value: unknown): string[] => {
  if (Array.isArray(value)) return value.map(String).filter(Boolean);
  if (typeof value === "string")
    return value.split(VALUE_DELIMITER).filter(Boolean);
  return [];
};

export const DynamicField = ({ field, name, required }: DynamicFieldProps) => {
  const { control } = useFormContext();

  const baseProps = {
    name: name,
    label: field.name,
    description: field.description,
    control,
  };

  const renderField = useMemo(() => {
    switch (field.type) {
      case "text":
        return (
          <Tooltip key={field.id} tooltipText={field.description}>
            <TextField
              {...baseProps}
              className="mx-auto w-lg"
              key={field.id}
              isRequired={required}
              placeholder={
                required
                  ? `Indtast ${field.name.toLowerCase()}`
                  : "Ikke påkrævet"
              }
            />
          </Tooltip>
        );
      case "dropdown":
        return (
          <Tooltip tooltipText={field.description}>
            <Controller
              key={field.id}
              control={control}
              name={name}
              render={({ field: formField }) => (
                <Select
                  className="mx-auto w-lg"
                  items={field.options.map((option) => ({
                    value: option.id,
                    label: option.value,
                  }))}
                  label={field.name}
                  isRequired={required}
                  placeholder={
                    required
                      ? `Vælg ${field.name.toLowerCase()}`
                      : "Ikke påkrævet"
                  }
                  selectedKey={formField.value as string | null}
                  onSelectionChange={formField.onChange}
                  aria-label={field.name}
                >
                  {(item) => (
                    <SelectItem key={item.value} id={item.value}>
                      {item.label}
                    </SelectItem>
                  )}
                </Select>
              )}
            />
          </Tooltip>
        );
      case "checkbox":
        return (
          <div
            key={field.id}
            className="mx-auto flex w-lg items-center justify-center gap-x-4"
          >
            <Tooltip tooltipText={field.description}>
              <Focusable>
                <div className="flex w-full items-center gap-x-4">
                  <CheckCard control={control} name={name} label={field.name} />
                </div>
              </Focusable>
            </Tooltip>
          </div>
        );
      case "multiselect":
        return (
          <Tooltip
            key={field.id}
            tooltipText={field.description ?? "Ingen beskrivelse"}
          >
            <Controller
              key={field.id}
              control={control}
              name={name}
              render={({ field: formField, fieldState: { error } }) => {
                const options = field.options.map((option) => ({
                  key: option.id,
                  label: option.value,
                  value: option.id,
                }));

                return (
                  <MultiSelect
                    label={field.name}
                    mode="compact"
                    items={options}
                    selectedKeys={new Set(parseSelectionValue(formField.value))}
                    onSelectionChange={(selectedKeys) => {
                      formField.onChange(
                        selectedKeys === "all"
                          ? options.map((option) => option.key)
                          : Array.from(selectedKeys),
                      );
                    }}
                    placeholder={
                      required
                        ? `Vælg ${field.name.toLowerCase()}`
                        : "Ikke påkrævet"
                    }
                    noCount
                    buttonClassName="mx-auto w-lg"
                    errorMessage={error?.message}
                  />
                );
              }}
            />
          </Tooltip>
        );
      case "number":
        return (
          <Tooltip tooltipText={field.description} key={field.id}>
            <NumberField
              className="mx-auto w-lg"
              key={field.id}
              {...baseProps}
            />
          </Tooltip>
        );
      case "date":
        return (
          <Tooltip tooltipText={field.description} key={field.id}>
            <DatePicker
              className="mx-auto w-lg"
              key={field.id}
              {...baseProps}
            />
          </Tooltip>
        );
      case "advanceddatetime":
        return (
          <Tooltip tooltipText={field.description} key={field.id}>
            <Controller
              name={baseProps.name}
              control={baseProps.control}
              render={({ field, fieldState }) => (
                <AdvancedDateTimeField
                  className="mx-auto w-lg"
                  {...field}
                  errorMessage={fieldState.error?.message}
                  label={baseProps.label}
                  onChange={field.onChange}
                />
              )}
            />
          </Tooltip>
        );
      default:
        console.warn(`Unknown field type: ${field.type}`);
        return null;
    }
  }, [field, name, control, required]);

  return renderField;
};
